/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
/*
 * Copied from plugins\techdocs\src\home\components\DefaultTechDocsHome.tsx and customized
 */

import { useMemo, useState } from 'react';
import {
  Content,
  Header,
  Page,
  SupportButton,
  Tab,
} from '@backstage/core-components';
import {
  CatalogFilterLayout,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  EntityListDocsTable,
  TechDocsIndexPageProps,
  TechDocsPicker,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import {
  ParentEntityRef,
  useCatalogHierarchy,
} from '../catalog/catalogByCategory/useCatalogHierarchy';
import { Box, Grid } from '@material-ui/core';
import SystemTreeNav from '../catalog/catalogByCategory/SystemTreeNav';
import { DomainTabs } from '../catalog/catalogByCategory/DomainTabs';
import { useRouteRef } from '@backstage/core-plugin-api';

/**
 * Component which renders a default documentation landing page.
 *
 * @public
 */
export const CustomTechDocsPage = (props: TechDocsIndexPageProps) => {
  const { initialFilter = 'owned', columns, actions } = props;
  const [selectedDomainTabIndex, setSelectedDomainTabIndex] = useState(0);
  const { catalogHierarchy } = useCatalogHierarchy();
  const rootRoute = useRouteRef(techdocsPlugin.routes.root);

  const tabs: Tab[] = useMemo(() => {
    return catalogHierarchy.map((item: ParentEntityRef) => {
      return {
        id: item.name,
        label: item.title || item.name,
      };
    });
  }, [catalogHierarchy]);

  const onTabChange = (tabIndex: number) => {
    setSelectedDomainTabIndex(tabIndex);
  };
  return (
    <Page themeId="documentation">
      <DomainTabs
        rootRoute={rootRoute()}
        tabs={tabs}
        selectedIndex={selectedDomainTabIndex}
        onChange={onTabChange}
      />
      <EntityListProvider>
        <Header title="Documentation">
          <CatalogFilterLayout.Filters
            options={{ drawerBreakpoint: 'xl', drawerAnchor: 'right' }}
          >
            <TechDocsPicker />
            <UserListPicker initialFilter={initialFilter} />
            <EntityOwnerPicker />
            <EntityTagPicker />
          </CatalogFilterLayout.Filters>

          <Box marginTop="8px" marginRight="8px">
            <SupportButton>
              Discover documentation in your ecosystem.
            </SupportButton>
          </Box>
        </Header>
        <Content>
          <Grid container style={{ position: 'relative' }}>
            {catalogHierarchy && catalogHierarchy.length > 0 && (
              <Grid item lg={3}>
                <SystemTreeNav
                  entities={
                    catalogHierarchy[selectedDomainTabIndex].children || []
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} lg={9}>
              <EntityListDocsTable actions={actions} columns={columns} />
            </Grid>
          </Grid>
        </Content>
      </EntityListProvider>
    </Page>
  );
};
